import { appConfig } from "@/constants";
import { EnvironmentNames } from "@/types";
import { alchemy } from "@/utils/alchemy";
import { BigNumber } from "alchemy-sdk";
import type { MainnetBalance } from "../types";

const SEPOLIA_TOKENS = [
  {
    symbol: "tIMX",
    address: "0x5b26a75eE4a4B68a8fe8f94E4b729Ff1b8a31051",
    name: "Test Immutable X",
    decimals: 18,
    imageUrl: "https://checkout-cdn.immutable.com/v1/blob/img/tokens/imx.svg",
  },
  {
    symbol: "USDC",
    address: "0x40b87d235A5B010a20A241F15797C9debf1ecd01",
    name: "USD Coin",
    decimals: 6,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0x6de8acc0d406837030ce4dd28e7c08c5a96a30d2.svg",
  },
  {
    symbol: "EURC",
    address: "0x08210F9170F89Ab7658F0B5E3fF39b0E03C594D4",
    name: "EURC",
    decimals: 6,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0x6de8acc0d406837030ce4dd28e7c08c5a96a30d2.svg",
  },
  {
    symbol: "tIMX",
    address: "0x2Fa06C6672dDCc066Ab04631192738799231dE4a",
    name: "Test Immutable X 2",
    decimals: 18,
    imageUrl: "https://checkout-cdn.immutable.com/v1/blob/img/tokens/imx.svg",
  },
];

const MAINNET_TOKENS = [
  {
    symbol: "IMX",
    address: "0xF57e7e7C23978C3cAEC3C3548E3D615c346e79fF",
    name: "IMX",
    decimals: 18,
    imageUrl: "https://checkout-cdn.immutable.com/v1/blob/img/tokens/imx.svg",
  },
  {
    symbol: "USDC",
    address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    name: "USD Coin",
    decimals: 6,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0x6de8acc0d406837030ce4dd28e7c08c5a96a30d2.svg",
  },
  {
    symbol: "USDT",
    address: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    name: "Tether USD",
    decimals: 6,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0x68bcc7f1190af20e7b572bcfb431c3ac10a936ab.svg",
  },
  {
    symbol: "GOD",
    address: "0xccc8cb5229b0ac8069c51fd58367fd1e622afd97",
    name: "Gods Unchained",
    decimals: 18,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0xe0e0981d19ef2e0a57cc48ca60d9454ed2d53feb.svg",
  },
  {
    symbol: "GOG",
    address: "0x9AB7bb7FdC60f4357ECFef43986818A2A3569c62",
    name: "Guild of Guardians",
    decimals: 18,
    imageUrl:
      "https://checkout-cdn.immutable.com/v1/blob/img/tokens/0xb00ed913aaff8280c17bff33cce82fe6d79e85e8.svg",
  },
  {
    symbol: "ILV",
    address: "0x767FE9EDC9E0dF98E07454847909b5E959D7ca0E",
    name: "Illuvium",
    decimals: 18,
    imageUrl: `${appConfig.ASSET_BASE_URL}/illuvium.webp`,
  },
  {
    symbol: "QUEST",
    address: "0x1Fc122FE8b6Fa6b8598799baF687539b5D3B2783",
    name: "Quest Ecosystem",
    decimals: 6,
    imageUrl: "https://cdn.ravenquest.io/assets/ravenquest_token-DudNXzGi.png",
  },
  {
    symbol: "IMT",
    address: "0xe2616122eD554bd693335E9143C47Df187a86EF3",
    name: "Immortal Token",
    decimals: 18,
    imageUrl: "https://brand.immortalrising2.com/images/imt.png",
  },
];

/**
 * Fetches Ethereum mainnet balances for a given wallet address.
 * Uses Alchemy to fetch token balances and native ETH balance.
 */
export async function getMainnetBalances(
  walletAddress: string,
): Promise<MainnetBalance[]> {
  if (!walletAddress) {
    return [];
  }

  const tokensToFetch =
    appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION
      ? MAINNET_TOKENS
      : SEPOLIA_TOKENS;
  const data = await alchemy.core.getTokenBalances(
    walletAddress,
    tokensToFetch.map((token) => token.address),
  );
  const balances: MainnetBalance[] = [];
  for (const balance of data.tokenBalances) {
    if (
      typeof balance.tokenBalance === "string" &&
      !BigNumber.from(balance.tokenBalance).eq(BigNumber.from(0))
    ) {
      const token = tokensToFetch.find(
        (t) =>
          t.address.toLocaleLowerCase() ===
          balance.contractAddress.toLocaleLowerCase(),
      );
      if (token) {
        balances.push({
          address: balance.contractAddress,
          symbol: token.symbol,
          balance: BigNumber.from(balance.tokenBalance).toString(),
          decimals: token.decimals,
          tokenName: token.name,
          tokenImage: token.imageUrl,
        });
      }
    }
  }
  const ethBalance = await alchemy.core.getBalance(walletAddress);
  if (!ethBalance.eq(BigNumber.from(0))) {
    balances.push({
      address: "ETH",
      symbol: "ETH",
      balance: ethBalance.toString(),
      decimals: 18,
      tokenName: "Ethereum",
      tokenImage:
        "https://checkout-cdn.immutable.com/v1/blob/img/tokens/eth.svg",
    });
  }
  return balances;
}
